import type {
  Client,
  Conversation,
  Participant,
  User,
} from "@twilio/conversations";

import { MESSAGE_CENTER_ACTIONS } from "./constants";
import { getSdkConversationObject } from "./conversationsObjects";
import type {
  ActionType,
  ChatConversation,
  SetParticipantsType,
  SetSidType,
  SetUnreadMessagesType,
} from "./types";

export async function loadUnreadMessagesCount(
  conversation: Conversation,
  updateUnreadMessages: SetUnreadMessagesType,
) {
  let count = 0;

  try {
    count =
      (await conversation.getUnreadMessagesCount()) ??
      (await conversation.getMessagesCount());
  } catch (e) {
    console.error("loadUnreadMessagesCount threw an error", e);
    throw e;
  }

  updateUnreadMessages(conversation.sid, count);
}

export async function handleParticipantsUpdate(
  participant: Participant,
  updateParticipants: SetParticipantsType,
) {
  const result = await participant.conversation.getParticipants();
  updateParticipants(result, participant.conversation.sid);
}

export async function readUserProfile(
  identity: string,
  client?: Client,
): Promise<User | undefined> {
  try {
    return await client?.getUser(identity);
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export async function updateCurrentConvo(
  setSid: SetSidType,
  convo: ChatConversation,
  updateParticipants: SetParticipantsType,
) {
  setSid(convo.sid);

  const participants = await getSdkConversationObject(convo).getParticipants();
  updateParticipants(participants, convo.sid);
}

export async function addConversation(
  name: string,
  additionalParticipantSids: string[],
  userId: string,
  updateParticipants: (participants: Participant[], sid: string) => void,
  conversations: ChatConversation[],
  client?: Client,
  isGroupChat: boolean = false,
) {
  if (!client) {
    throw new Error("Client is missing");
  }

  if (name.length === 0) {
    throw new Error("Conversation name is empty");
  }

  try {
    for (const conversation of conversations) {
      const convo = getSdkConversationObject(conversation);
      const participants = await convo.getParticipants();
      const participantIds = participants.map((p) => p.identity);

      if (
        (isGroupChat &&
          existsGroupConversation(
            additionalParticipantSids,
            participantIds,
            userId,
          )) ||
        (!isGroupChat &&
          existsOneOnOneConversation(
            additionalParticipantSids,
            participantIds,
            userId,
          ))
      ) {
        return convo;
      }
    }

    const conversation = await client.createConversation({
      friendlyName: name,
    });

    await conversation.join();

    for (const participantSid of additionalParticipantSids) {
      await conversation.add(participantSid);
    }

    const participants = await conversation.getParticipants();
    updateParticipants(participants, conversation.sid);

    return conversation;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export async function updateConversation(
  convo: Conversation | undefined,
  newConversationName: string | undefined,
  additionalParticipantSids: string[],
  updateParticipants: (participants: Participant[], sid: string) => void,
  client?: Client,
  isRemoving: boolean = false,
) {
  if (!client) {
    throw new Error("Client is missing");
  }

  if (!convo) {
    throw new Error("Conversation is missing");
  }

  try {
    const conversation = await client.getConversationBySid(convo.sid);

    if (!conversation) {
      throw new Error("Conversation not found");
    }

    if (
      newConversationName &&
      newConversationName !== "" &&
      newConversationName !== conversation.friendlyName
    ) {
      await conversation.updateFriendlyName(newConversationName);
    }

    const participants = await conversation.getParticipants();
    const participantIds = participants.map((p) => p.identity);

    for (const participantSid of additionalParticipantSids) {
      if (isRemoving && participantIds.includes(participantSid)) {
        await conversation.removeParticipant(participantSid);
      } else if (!participantIds.includes(participantSid)) {
        await conversation.add(participantSid);
      }
    }

    const updatedParticipants = await conversation.getParticipants();
    updateParticipants(updatedParticipants, conversation.sid);

    return conversation;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export async function sendNotifications(
  participant: Participant,
  client: Client,
  actionType: ActionType,
) {
  try {
    const conversation = participant.conversation;
    const user = await participant.getUser();
    let message: string;

    switch (actionType) {
      case MESSAGE_CENTER_ACTIONS.ADD_NEW_PARTICIPANT: {
        message = `${user.friendlyName ?? user.identity} has been added to the channel`;
        break;
      }
      case MESSAGE_CENTER_ACTIONS.REMOVE_PARTICIPANT: {
        message = `${user.friendlyName ?? user.identity} is no longer in the channel`;
        break;
      }
      default:
        message = "Unknown action type";
        break;
    }

    const newMessageBuilder = conversation
      .prepareMessage()
      .setBody(message)
      .setAttributes({ actionType });

    const messageIndex = await newMessageBuilder.build().send();

    await conversation.advanceLastReadMessageIndex(messageIndex ?? 0);
  } catch (error: any) {
    console.error(error);
    throw error;
  }
}

function existsGroupConversation(
  additionalParticipantSids: string[],
  participantIds: (string | null)[],
  userId: string,
): boolean {
  return (
    participantIds.length > 2 &&
    participantIds.includes(userId) &&
    additionalParticipantSids.every((sid) => participantIds.includes(sid))
  );
}

function existsOneOnOneConversation(
  additionalParticipantSids: string[],
  participantIds: (string | null)[],
  userId: string,
): boolean {
  return (
    participantIds.length === 2 &&
    participantIds.includes(userId) &&
    participantIds.includes(additionalParticipantSids[0] as string)
  );
}

export const handlePromiseRejection = async (
  ...funcs: Array<() => Promise<any> | void>
): Promise<void> => {
  try {
    await Promise.all(
      funcs.map((func) => {
        const result = func();
        if (result instanceof Promise) {
          return result;
        }
        return Promise.resolve(result);
      }),
    );
  } catch (e) {
    console.error(e);
    throw e;
  }
};
